import React from 'react';
import styled from 'styled-components';
import { ModuleType } from '../types';

import {
  cssColorValue,
  screenXxsMax,
  screenXsMin,
  screenSmMin,
  screenSmMax,
  screenMdMin,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
  spacer40,
  spacer56,
  Type,
} from '@spotify-internal/encore-web';
import { articleMarkdown, defaultMarkdown } from '../../Markdown';
import { ShareButtonsComponent } from '../../ui/ShareButtons';
import { hexToRgba } from '../../../../features/src/color-conversion';
import { getLinkStyle, ImageAspectBox } from '../../ui';
import { useT } from '@spotify-internal/i18n-core';

const maxWidthOfImage = '800px';
const maxWidthOfText = '664px';

const Article = styled.div`
  @media (max-width: ${screenXxsMax}) {
    margin-left: ${spacer16};
    margin-right: ${spacer16};
    margin-bottom: ${spacer40};
  }

  @media (min-width: ${screenXsMin}) and (max-width: ${screenSmMax}) {
    margin-left: 52px;
    margin-right: 52px;
    margin-bottom: ${spacer16};
  }

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}) {
    width: ${maxWidthOfText};
    margin: 0 auto ${spacer16};
  }

  @media (min-width: ${screenMdMin}) {
    width: ${maxWidthOfImage};
    margin: 0 auto ${spacer16};
  }
`;

const IntroductionWrapper = styled.div`
  padding-bottom: 0;

  @media (min-width: ${screenMdMin}) {
    max-width: 664px;
    margin: auto;
  }

  @media (max-width: ${screenXxsMax}) {
    margin-bottom: ${spacer24};
  }

  @media (min-width: ${screenXsMin}) {
    margin-bottom: ${spacer32};
  }
`;

const Introduction = styled(Type).attrs({
  variant: 'alto',
})`
  @media (max-width: ${screenXxsMax}) {
    font-size: 1.75rem;
    line-height: 2.25rem;
  }

  @media (min-width: ${screenXsMin}) {
    font-size: 2rem;
    line-height: 2.5rem;
  }

  a {
    color: ${cssColorValue('textBase')};
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
`;

const BodyWrapper = styled.div``;

const Body = styled.div`
  @media (min-width: ${screenMdMin}) {
    & > * {
      width: ${maxWidthOfText};
      margin: auto;
    }

    p.article-picture,
    div {
      width: ${maxWidthOfImage};
    }
  }

  iframe {
    display: block;
    width: 100%;
    margin: auto;
    margin-bottom: ${spacer32};
  }

  ${ImageAspectBox} {
    width: 100%;
    aspect-ratio: 16 / 9;
    margin: auto;

    iframe {
      height: 100%;
      padding-bottom: 0;
    }
  }

  img,
  div > iframe {
    width: 100%;
    margin-bottom: ${spacer40};
  }

  figcaption {
    text-align: left;
  }

  a {
    ${props => getLinkStyle(props)}
    text-decoration: underline;
  }

  ul,
  ol {
    padding-left: ${spacer24};
  }

  ol > li,
  ul > li {
    margin-bottom: ${spacer16};
  }

  p {
    margin-top: 0;
  }

  p,
  ul,
  ol {
    padding-block-end: 0;
    margin-bottom: ${spacer24};
  }

  h1,
  h2,
  h3,
  h4 {
    margin-top: 0;
  }

  h3 {
    margin-bottom: ${spacer16};
  }

  blockquote {
    border: 0;
    padding: 0;
    margin: ${spacer24} auto;

    p {
      color: ${cssColorValue('textBrightAccent')};
      font-weight: 450;
      letter-spacing: -0.02em;
    }
  }

  @media (max-width: ${screenXxsMax}) {
    margin-bottom: ${spacer40};

    p + :not(p, blockquote, ul, ol),
    p + .article-picture {
      margin-top: ${spacer40};
    }

    h2 {
      font-size: 1.75rem;
      line-height: 2.25rem;
    }

    h3 {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    p,
    ol > li,
    ul > li {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    blockquote p {
      font-size: 1.625rem;
      line-height: 2.25rem;
    }
  }

  @media (min-width: ${screenXsMin}) {
    margin-bottom: ${spacer56};

    p + :not(p, blockquote, ul, ol),
    p + .article-picture {
      margin-top: ${spacer56};
    }

    h2 {
      font-size: 2rem;
      line-height: ${spacer40};
    }

    h3 {
      font-size: 1.25rem;
      line-height: ${spacer32};
    }

    p,
    ol > li,
    ul > li {
      font-size: 1.25rem;
      line-height: ${spacer32};
    }

    blockquote p {
      font-size: 1.75rem;
      line-height: ${spacer40};
    }
  }
`;

const BottomSection = styled.div`
  @media (min-width: ${screenMdMin}) {
    width: ${maxWidthOfText};
    margin: auto;
  }

  display: flex;
  flex-direction: column;

  border-top: 1px solid ${({ theme }) => theme.box.text};
  padding-top: ${spacer32};

  p {
    margin-bottom: ${spacer40};
  }
`;

const ShareButtonContainer = styled.div`
  display: flex;
  flex-direction: column;

  & > span {
    margin-bottom: ${spacer12};
  }

  & > div {
    margin-top: -2px;
    padding-bottom: 0;

    svg {
      color: ${({ theme }) => theme.box.text};
      &:hover {
        color: ${({ theme }) => hexToRgba(theme.box.text, 0.5)};
      }
    }
  }
`;

export type ArticleModuleType = ModuleType & {
  introduction?: string;
  body?: string;
  initialDropcap?: boolean;
};

export const ArticleModule: React.FC<ArticleModuleType> = ({
  introduction = '',
  body = '',
  initialDropcap = false,
}) => {
  const t = useT();
  const markDown = articleMarkdown(body, initialDropcap);

  return (
    <Article>
      <IntroductionWrapper>
        <Introduction
          dangerouslySetInnerHTML={{
            __html: defaultMarkdown(introduction),
          }}
        />
      </IntroductionWrapper>
      <BodyWrapper>
        <Body
          dangerouslySetInnerHTML={{
            __html: markDown,
          }}
        />
      </BodyWrapper>
      <BottomSection>
        <Type as="p" variant="ballad">
          {t(
            'FRODOR_e7365a',
            'Spotify for Artists helps you to develop the fanbase you need to reach your goals.',
            '',
          )}
        </Type>
        <ShareButtonContainer>
          <Type variant="balladBold">
            {t(
              'FRODOR_f92bd3',
              'Share this story',
              'Share refers to sharing on social media',
            )}
          </Type>
          <ShareButtonsComponent />
        </ShareButtonContainer>
      </BottomSection>
    </Article>
  );
};
